import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { ImagePlus, VideoIcon, Loader2, Plus, X } from 'lucide-react';
import { CourseMediaItem } from '../../types';
import { mediaService } from '../../services/media';
import { useToast } from '../../hooks/useToast';
import { VideoInput, VideoCoverSelector } from '../media';
import Modal from '../common/Modal';
import GalleryItemCard from './GalleryItemCard';
import { newId } from './util';

const MAX_IMAGES = 5;
const MAX_VIDEOS = 1;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB per image (matches backend MAX_FILE_SIZE)
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB — gallery videos are short preview clips, not lesson content

interface MediaGalleryEditorProps {
  items: CourseMediaItem[];
  onChange: (items: CourseMediaItem[]) => void;
  courseId?: string;
  saving?: boolean;
}

interface TempVideo {
  videoUrl: string;
  videoId?: string;
  videoStatus?: 'uploading' | 'processing' | 'ready' | 'failed';
}

const EMPTY_VIDEO: TempVideo = { videoUrl: '' };

export default function MediaGalleryEditor({ items, onChange, courseId, saving = false }: MediaGalleryEditorProps) {
  const { t } = useTranslation('teaching');
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [temp, setTemp] = useState<TempVideo>(EMPTY_VIDEO);
  const [videoKey, setVideoKey] = useState(0); // remount VideoInput to reset it after adding
  const [videoProgress, setVideoProgress] = useState<Record<string, number>>({}); // mediaId → processing %
  const [coverEditId, setCoverEditId] = useState<string | null>(null); // gallery item whose video cover is being chosen

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const imageCount = items.filter((i) => i.type === 'image').length;
  const videoCount = items.filter((i) => i.type === 'video').length;
  const imagesFull = imageCount >= MAX_IMAGES;
  const videosFull = videoCount >= MAX_VIDEOS;

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0 || !courseId) return;
    const picked = Array.from(files);
    // Enforce the per-image size cap client-side (backend also rejects oversized images).
    const withinSize = picked.filter((f) => f.size <= MAX_IMAGE_SIZE);
    if (withinSize.length < picked.length) {
      toast.error(t('courseEdit.imageTooLarge', { size: `${MAX_IMAGE_SIZE / 1024 / 1024}MB` }));
    }
    const toUpload = withinSize.slice(0, MAX_IMAGES - imageCount);
    if (toUpload.length === 0) {
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setUploading(true);
    try {
      const added: CourseMediaItem[] = [];
      for (const file of toUpload) {
        const res = await mediaService.uploadThumbnail(file, 'course', courseId);
        added.push({ id: newId(), type: 'image', url: res.url });
      }
      onChange([...items, ...added]);
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('courseEdit.uploadFailed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  function closePanel() {
    setTemp(EMPTY_VIDEO);
    setVideoKey((k) => k + 1);
    setShowVideo(false);
  }

  // An uploaded video is added to the gallery (and auto-saved) the instant its upload completes —
  // not when the user clicks "Add to Gallery" — so refreshing mid-processing can't lose it. The
  // poll effect below then drives it to ready. A link video stays staged until "Add to Gallery".
  function handleVideoInputChange(videoUrl: string, videoId?: string, videoStatus?: TempVideo['videoStatus']) {
    if (!videoId || videoStatus === 'uploading' || videoStatus === 'failed') {
      setTemp((p) => ({ ...p, videoUrl, videoId, videoStatus }));
      return;
    }
    if (!items.some((i) => i.mediaId === videoId)) {
      onChange([...items, { id: newId(), type: 'video', videoSource: 'upload', mediaId: videoId, videoStatus }]);
    }
    closePanel();
  }

  function addLinkVideo() {
    if (temp.videoId || !temp.videoUrl) return;
    onChange([...items, { id: newId(), type: 'video', videoSource: 'link', url: temp.videoUrl }]);
    closePanel();
  }

  // Finish processing uploaded videos — including any reloaded after a page refresh — by polling
  // their status, then persist the result (auto-save) so the gallery reflects ready/failed. On
  // ready we default the poster to a mid-video frame so the item shows a thumbnail.
  useEffect(() => {
    const pending = items.filter(
      (i) => i.type === 'video' && i.videoSource === 'upload' && i.mediaId &&
        (i.videoStatus === 'processing' || i.videoStatus === 'uploading')
    );
    if (pending.length === 0) return;
    let cancelled = false;
    const poll = async () => {
      for (const it of pending) {
        try {
          const s = await mediaService.getVideoStatus(it.mediaId!);
          if (cancelled) return;
          setVideoProgress((prev) => ({ ...prev, [it.mediaId!]: s.progress }));
          if (s.status !== 'ready' && s.status !== 'failed') continue;
          let posterUrl = it.posterUrl;
          if (s.status === 'ready' && !posterUrl) {
            try {
              const { frames } = await mediaService.getVideoFrames(it.mediaId!);
              if (frames.length) posterUrl = frames[Math.floor(frames.length / 2)].url;
            } catch { /* no frames — item falls back to a play icon */ }
          }
          if (cancelled) return;
          onChange(items.map((x) => (x.id === it.id ? { ...x, videoStatus: s.status, posterUrl } : x)));
        } catch { /* transient error — retry on the next tick */ }
      }
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [items, onChange]);

  const coverEditItem = coverEditId ? items.find((i) => i.id === coverEditId) : null;

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(items, oldIndex, newIndex));
  }

  function removeAt(id: string) {
    onChange(items.filter((i) => i.id !== id));
  }

  function makeCover(id: string) {
    const idx = items.findIndex((i) => i.id === id);
    if (idx <= 0) return;
    onChange(arrayMove(items, idx, 0));
  }

  return (
    <div className="glass-card rounded-lg p-5">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-gray-900">{t('courseEdit.mediaGallery')}</h2>
        <div className="flex items-center gap-2">
          {saving && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
              <Loader2 className="w-3 h-3 animate-spin" />{t('courseEdit.gallerySaving')}
            </span>
          )}
          <span className="text-xs text-gray-500">{t('courseEdit.galleryCount', { images: imageCount, maxImages: MAX_IMAGES, videos: videoCount, maxVideos: MAX_VIDEOS })}</span>
        </div>
      </div>
      <p className="text-sm text-gray-500 mb-1">{t('courseEdit.galleryHint')}</p>
      <p className="text-xs text-gray-400 mb-4">{t('courseEdit.galleryLimits', { maxImages: MAX_IMAGES, maxVideos: MAX_VIDEOS, imageSize: `${MAX_IMAGE_SIZE / 1024 / 1024}MB`, videoSize: `${MAX_VIDEO_SIZE / 1024 / 1024}MB` })}</p>

      {/* Add controls */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={imagesFull || uploading || !courseId}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          {t('courseEdit.addImage')}
        </button>
        <button
          type="button"
          onClick={() => setShowVideo((v) => !v)}
          disabled={videosFull || !courseId}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <VideoIcon className="w-4 h-4" />
          {t('courseEdit.addVideo')}
        </button>
        {imagesFull && <span className="self-center text-xs text-amber-600">{t('courseEdit.imagesFull', { count: MAX_IMAGES })}</span>}
        {videosFull && <span className="self-center text-xs text-amber-600">{t('courseEdit.videoFull', { count: MAX_VIDEOS })}</span>}
      </div>

      {/* Add-video panel */}
      {showVideo && !videosFull && (
        <div className="mb-5 p-4 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">{t('courseEdit.addVideo')}</span>
            <button type="button" onClick={() => { setShowVideo(false); setTemp(EMPTY_VIDEO); }} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
          </div>
          <VideoInput
            key={videoKey}
            videoUrl={temp.videoUrl}
            videoId={temp.videoId}
            videoStatus={temp.videoStatus}
            onChange={handleVideoInputChange}
            targetType="course"
            targetId={courseId}
            maxSize={MAX_VIDEO_SIZE}
          />
          <p className="mt-2 text-xs text-gray-400">{t('courseEdit.videoAutoSaveHint')}</p>
          <button type="button" onClick={addLinkVideo} disabled={!!temp.videoId || !temp.videoUrl} className="mt-3 inline-flex items-center gap-1.5 btn-gold px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed">
            <Plus className="w-4 h-4" />{t('courseEdit.addToGallery')}
          </button>
        </div>
      )}

      {/* Grid */}
      {items.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
          <p className="text-gray-500 text-sm">{t('courseEdit.galleryEmpty')}</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map((i) => i.id)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {items.map((item, idx) => (
                <GalleryItemCard
                  key={item.id}
                  item={item}
                  isCover={idx === 0}
                  progress={item.mediaId ? videoProgress[item.mediaId] : undefined}
                  onRemove={() => removeAt(item.id)}
                  onMakeCover={() => makeCover(item.id)}
                  onChooseCover={item.type === 'video' && item.videoSource === 'upload' && item.mediaId ? () => setCoverEditId(item.id) : undefined}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Per-video cover (poster frame) picker */}
      {coverEditItem?.mediaId && (
        <Modal isOpen onClose={() => setCoverEditId(null)} title={t('courseEdit.chooseCover')} size="lg">
          <VideoCoverSelector
            mediaId={coverEditItem.mediaId}
            onSelect={(posterUrl) => onChange(items.map((i) => (i.id === coverEditItem.id ? { ...i, posterUrl } : i)))}
          />
        </Modal>
      )}
    </div>
  );
}
